import { browserName, browserVersion, engineName, engineVersion, mobileModel, mobileVendor, osName, osVersion, isMobile } from "react-device-detect";

import { APP_TENANT, KEYS } from "@/src/const";
import { TrackingLogic } from "@/src/logic";
import { FlowFrontendService } from "@/src/service";
import { TrackingType, StrapiComponentType } from "@/src/type";
import { Log } from "@/src/util";

declare global {
  interface Window {
    _hsq?: any;
    gtag?: any;
    fbq?: any;
  }
}

type PushToType = {
  hubspot?: boolean;
  googleAnalytics?: boolean | string;
  googleAds?: boolean | string;
  googleAdsConversionLabel?: string;
  meta?: string;
  action?: string | null;
  actionId?: string | null;
  actionIdType?: string | null;
  actionId2?: string | null;
  actionId2Type?: string | null;
  actionMessage?: string | null;
  triggeredFrom?: string | null;
};

function windowExists() {
  return typeof window !== "undefined";
}

function pushToTrackingEvents(pushTo: PushToType) {
  if (windowExists()) {
    const _action = pushTo.googleAnalytics === "string" ? pushTo.googleAnalytics : pushTo.action;
    if (pushTo?.hubspot && window._hsq) {
      window._hsq.push([
        "trackCustomBehavioralEvent",
        {
          name: _action,
          properties: {
            actionId: pushTo.actionId,
            actionIdType: pushTo.actionIdType,
            actionId2: pushTo.actionId2,
            actionId2Type: pushTo.actionId2Type,
            actionMessage: pushTo.actionMessage,
            triggeredFrom: pushTo.triggeredFrom,
          },
        },
      ]);
    }

    if (pushTo?.googleAnalytics && typeof window.gtag === "function") {
      window.gtag("event", _action, {
        actionId: pushTo.actionId,
        actionIdType: pushTo.actionIdType,
        actionId2: pushTo.actionId2,
        actionId2Type: pushTo.actionId2Type,
        actionMessage: pushTo.actionMessage,
        triggeredFrom: pushTo.triggeredFrom,
      });
    }

    if (pushTo?.googleAds && typeof window.gtag === "function") {
      const defaultLabel = localStorage.getItem(KEYS.LOCAL_STORAGE.CONVERSION_LABEL);
      const conversionLabel = pushTo?.googleAdsConversionLabel ?? defaultLabel;
      if (conversionLabel) {
        window.gtag("event", "conversion", { send_to: conversionLabel });
      }
    }

    if (pushTo?.meta && typeof window.fbq === "function") {
      window.fbq("trackCustom", `${pushTo.meta}`, {
        action: pushTo.action,
        actionId: pushTo.actionId,
        actionIdType: pushTo.actionIdType,
        actionId2: pushTo.actionId2,
        actionId2Type: pushTo.actionId2Type,
        actionMessage: pushTo.actionMessage,
        triggeredFrom: pushTo.triggeredFrom,
      });
    }
  }
}

function getPropertySlug() {
  try {
    return window.location.pathname.includes("/properties/") ? window.location.pathname.split("/properties/")[1].split("/")[0] : undefined;
  } catch (err) {
    return undefined;
  }
}

export interface PostTrackingLogInput {
  action: string | "page_view" | "click" | "download" | null;
  actionId?: string | null;
  actionIdType?: string | null;
  actionId2?: string | null;
  actionId2Type?: string | null;
  actionMessage?: string | null;
  triggeredFrom?: string | null;
  pushTo?: {
    hubspot?: boolean;
    googleAnalytics?: boolean | string;
    googleAds?: boolean | string;
    googleAdsConversionLabel?: string;
    meta?: string;
  };
  trackingEvents?: StrapiComponentType.Object.TrackingEvent[];
}

type PartialTrackingLog = Omit<TrackingType.Tracking, "sessionId" | "deviceId" | "source" | "domain">;

async function postTrackingLog({
  action,
  actionId,
  actionIdType,
  actionId2,
  actionId2Type,
  actionMessage,
  triggeredFrom,
  pushTo,
  trackingEvents,
}: PostTrackingLogInput) {
  const sessionId = await TrackingLogic.getSessionId();
  const deviceId = TrackingLogic.getDeviceId();

  const urlData: PartialTrackingLog = {
    url: window.location.href,
    pathname: window.location.pathname,
    queryParams: window.location.search,
  };

  //Should width/height be the viewport size or the screen size?
  const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

  const deviceData: PartialTrackingLog = {
    browserName: browserName,
    browserVersion: browserVersion,
    browserEngine: engineName,
    browserEngineVersion: engineVersion,
    osName: osName,
    osVersion: osVersion,
    mobileBrand: mobileVendor,
    mobileBrandModel: mobileModel,
    deviceHeight: height,
    deviceWidth: width,
  };
  const locationData: PartialTrackingLog = (await TrackingLogic.getLocationData()) ?? {};
  if (locationData?.createdAt) {
    delete locationData.createdAt;
  }
  const brokerProfile = new URLSearchParams(window.location.search).get(KEYS.SEARCH_PARAM.BROKER_PROFILE) ?? undefined;

  const trackingLog: TrackingType.Tracking = {
    sessionId,
    persistentId: TrackingLogic.getPersistentId(),
    domain: window.location.host,
    source: APP_TENANT,
    deviceId,
    brokerProfile,
    ...urlData,
    ...deviceData,
    ...locationData,
    utmSource: TrackingLogic.getUTMParams().utmSource,
    utmMedium: TrackingLogic.getUTMParams().utmMedium,
    utmCampaign: TrackingLogic.getUTMParams().utmCampaign,
    utmTerm: TrackingLogic.getUTMParams().utmTerm,
    utmContent: TrackingLogic.getUTMParams().utmContent,
    facebookCLID: TrackingLogic.getUTMParams().facebookCLID,
    googleCLID: TrackingLogic.getUTMParams().googleCLID,
    referrer: TrackingLogic.getUTMParams().referrer,
    marketingSourceName: TrackingLogic.getMarketingSource(),
    localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    localTime: new Date().toLocaleTimeString(),
    action,
    actionId,
    actionIdType,
    actionId2,
    actionId2Type,
    actionMessage,
    appType: isMobile ? "mobileWeb" : "desktopWeb",
    triggeredFrom,
    propertySlug: getPropertySlug(),
  };

  if (pushTo && !trackingEvents) {
    pushToTrackingEvents({
      ...pushTo,
      action: action,
      actionId: actionId,
      actionIdType: actionIdType,
      actionId2: actionId2,
      actionId2Type: actionId2Type,
      actionMessage: actionMessage,
    });
  }

  if (trackingEvents) {
    (trackingEvents ?? []).forEach((event) =>
      pushToTrackingEvents({
        googleAnalytics: event.gaEventName,
        googleAds: event.googleAdsConversion,
        googleAdsConversionLabel: event.googleAdsConversionLabel,
        meta: event.fbEventName,
        hubspot: true,
        action: action,
        actionId: actionId,
        actionIdType: actionIdType,
        actionId2: actionId2,
        actionId2Type: actionId2Type,
        actionMessage: actionMessage,
      })
    );
  }

  Log.logToConsoleDebug("FrontendTrackingService.ts:(postTrackingLog)", "emitting tracking data", [trackingLog]);
  return FlowFrontendService.http("POST", "/v1/tracking", undefined, trackingLog);
}

export default { postTrackingLog };
